import React, { lazy, Suspense } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProtectedRoute, {
  ProtectedRouteWithPermission,
} from "./components/ProtectedRoute.js";
import PublicRoute from "./components/PublicRoute.js";
import { AuthProvider } from "./components/login/AuthProvider.js";
import { CurrencyProvider } from "./context/currencyContext.js";
import ManualsPage from "./pages/manualPage.js";
import ManualEditor from "./pages/Manual/ManualEditor.js";
import ReminderProvider from "./components/ReminderProvider/ReminderProvider.js";
import { UploadManagerProvider } from "./context/UploadManagerContext";

// All route-level pages are lazy-loaded so each route only downloads its own code chunk
// instead of every page (and every heavy library any page imports, e.g. three.js)
// being bundled into one giant file that every route has to load.
const SigninForm = lazy(() => import("./pages/login"));
const Register = lazy(() => import("./pages/register"));
const SignUpStep2 = lazy(() => import("./components/register/signupstep2"));
const Signupstep3 = lazy(() => import("./components/register/signupstep3"));
const Home = lazy(() => import("./pages/home"));
const CompleteTask = lazy(() => import("./components/home/actionButton/completeTask.js"));
const SimpleMode = lazy(() => import("./components/home/actionButton/simpleMode.js"));
const DriverPage = lazy(() => import("./components/home/actionButton/DriverPage.js"));
const Keyboard = lazy(() => import("./components/header/help/keyboard"));
const Support = lazy(() => import("./components/header/help/support"));
const AddAccount = lazy(() => import("./components/header/admin/addAccount"));
const CasesCancelledReportView = lazy(() => import("./components/dashboard/reports/CaseMangement/CasesCancelledReportView.js"));
const PrintWorkTicketSticker = lazy(() => import("./components/dashboard/cases/newCase/case_id/actionButton/PrintWorkTicketSticker.js"));
const PrintWorkTicket = lazy(() => import("./components/dashboard/cases/newCase/case_id/actionButton/PrintWorkTicket.js"));
const User = lazy(() => import("./components/header/admin/myAccount/user"));
const Calender = lazy(() => import("./components/header/hiraDentalLab/calender"));
const File = lazy(() => import("./components/header/hiraDentalLab/files/fileSetting"));
const Addnewfile = lazy(() => import("./components/header/hiraDentalLab/files/addNewfile"));
const LabSetting = lazy(() => import("./components/header/hiraDentalLab/labSetting"));
const Users = lazy(() => import("./components/header/hiraDentalLab/users/users"));
const AddNewUser = lazy(() => import("./components/header/hiraDentalLab/users/addNewUser"));
const DetailTabs = lazy(() => import("./components/header/hiraDentalLab/labDetails/tabs"));
const Overview = lazy(() => import("./components/dashboard/cases/overview"));
const AccountDetails = lazy(() => import("./components/dashboard/cases/newCase/accountDetails"));
const NewPlanEstimate = lazy(() => import("./components/dashboard/cases/newPlanEstimate/newPlanEstimate"));
const NewCasePickup = lazy(() => import("./components/dashboard/cases/casePickup/newCasePickup"));
const CasesOpened = lazy(() => import("./components/dashboard/cases/byMonth/casesOpened"));
const CaseShip = lazy(() => import("./components/dashboard/cases/ship/caseShip"));
const CreatShipment = lazy(() => import("./components/dashboard/cases/shipment/creatShipment.js"));
const OpenTryInCases = lazy(() => import("./components/dashboard/cases/tryIns/openTryIns.js"));
const CasesOnHold = lazy(() => import("./components/dashboard/cases/onHold/casesOnHold.js"));
const ToOutsource = lazy(() => import("./components/dashboard/cases/outsource/toOutsource.js"));
const CaseEstimates = lazy(() => import("./components/dashboard/cases/planEstimate/caseEstimates.js"));
const OpenCases = lazy(() => import("./components/dashboard/cases/openCases/openCases.js"));
const UndeliverableCases = lazy(() => import("./components/dashboard/cases/undeliarableCases/undelivarableCases.js"));
const Addaccountsales = lazy(() => import("./components/dashboard/reports/Sales/Addaccountsale.js"));
const PasswordReset = lazy(() => import("./components/login/Reset.js"));
const ResetPasswordConfirm = lazy(() => import("./components/login/resetPasswirdConfirm.js"));
const SettingOverview = lazy(() => import("./components/dashboard/settings/overview.js"));
const SearchAddPage = lazy(() => import("./components/dashboard/settings/accounts/searchAddPage.js"));
const AccountTable = lazy(() => import("./components/dashboard/settings/accounts/accountsTable.js"));
const AddContactButton = lazy(() => import("./components/dashboard/settings/accounts/tabs/addContactButton.js"));
const EditContactButton = lazy(() => import("./components/dashboard/settings/accounts/tabs/editContactButton.js"));
const TabsWorking = lazy(() => import("./components/dashboard/settings/accounts/tabs/tabsWorking.js"));
const FinanceOwerview = lazy(() => import("./components/dashboard/finances/financeOverview.js"));
const Posting = lazy(() => import("./components/dashboard/finances/posting/posting.js"));
const Statement = lazy(() => import("./components/dashboard/finances/statement/statements.js"));
const CaseSetting = lazy(() => import("./components/dashboard/settings/caseSetting/caseSetting.js"));
const RunStatements = lazy(() => import("./components/dashboard/finances/statement/runStatement.js"));
const PromotionOverview = lazy(() => import("./components/dashboard/promotions/promotionOverview.js"));
const DDXWebsites = lazy(() => import("./components/dashboard/promotions/websites/website.js"));
const DDXAdd = lazy(() => import("./components/dashboard/promotions/ads/ddxAdd.js"));
const AddButton = lazy(() => import("./components/dashboard/promotions/ads/addButton.js"));
const Coupon = lazy(() => import("./components/dashboard/promotions/coupons/coupon.js"));
const AddCoupon = lazy(() => import("./components/dashboard/promotions/coupons/addCoupon.js"));
const Widgets = lazy(() => import("./components/dashboard/promotions/widgets/widgets.js"));
const DDXAccount = lazy(() => import("./components/dashboard/promotions/ddxAccount/ddxAccount.js"));
const Case = lazy(() => import("./components/dashboard/cases/newCase/case_id/case.js"));
const EditUser = lazy(() => import("./components/header/admin/EditUser/editUser.js"));
const EditAuthentication = lazy(() => import("./components/header/admin/EditUser/editauthentication.js"));
const PatientRecord = lazy(() => import("./components/dashboard/cases/PatientRecord.js"));
const InvoiceDetail = lazy(() => import("./components/dashboard/cases/newCase/invoiceDetails.js"));
const AddLabProcedureReportTable = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/AddLabProcedure.js"));
const EditInvoice = lazy(() => import("./components/dashboard/cases/newCase/EditInvoice.js"));
const PermissionGroups = lazy(() => import("./components/dashboard/settings/permissionGroup/permissionGroups.js"));
const AddPermissionGroup = lazy(() => import("./components/dashboard/settings/permissionGroup/addPermissionGroup.js"));
const Reports = lazy(() => import("./components/dashboard/reports/reports.js"));
const AddSalesReport = lazy(() => import("./components/dashboard/reports/Sales/AddSalesReport.js"));
const Addinvoiceitem = lazy(() => import("./components/dashboard/reports/Sales/Addinvoice.js"));
const PartnerLab = lazy(() => import("./components/dashboard/partnerLab.js"));
const Reprintbutton = lazy(() => import("./components/dashboard/finances/statement/reprintbutton.js"));
const ReprintStatements = lazy(() => import("./components/dashboard/finances/statement/reprintStatement.js"));
const Summary = lazy(() => import("./components/dashboard/reports/Finances/Summary.js"));
const RunDateRangeStatement = lazy(() => import("./components/dashboard/finances/statement/RunDateRangeStatement.js"));
const EditPermission = lazy(() => import("./components/header/admin/EditUser/editPermission.js"));
const AddCategory = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/AddCategory .js"));
const CasesArrive = lazy(() => import("./components/dashboard/cases/arrive/casesArrive .js"));
const BarcodeList = lazy(() => import("./components/dashboard/reports/CaseMangement/BarcodeList.js"));
const BatchPrintWorkTicketsForm = lazy(() => import("./components/dashboard/reports/CaseMangement/batchprintworkticketsform.js"));
const TicketsForm = lazy(() => import("./components/dashboard/reports/CaseMangement/ticketsform.js"));
const MoneyOwedOverdue = lazy(() => import("./components/dashboard/reports/Finances/money-owed-overdue.js"));
const Addshipping = lazy(() => import("./components/dashboard/reports/CaseMangement/Addshipping.js"));
const CaseFiles = lazy(() => import("./components/dashboard/reports/CaseMangement/caseFile.js"));
const EditAttributePrices = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/EditAttributePrices.js"));
const EditPriceLists = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/EditPriceLists.js"));
const PriceListTable = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/PriceListTable.js"));
const ProcedureQuickCodes = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/ProcedureQuickCodes.js"));
const ProcedureGroups = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/ProcedureGroups.js"));
const AddProcedureGroup = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/AddProcedureGroup.js"));
const MultiEdit = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/MultiEdit.js"));
const AssignPrice = lazy(() => import("./components/dashboard/settings/accounts/AssignPrice.js"));
const AssignProcedure = lazy(() => import("./components/dashboard/settings/accounts/AssignProcedure.js"));
const AssignShippingService = lazy(() => import("./components/dashboard/settings/accounts/AssignShippingService.js"));
const AssignAccountTags = lazy(() => import("./components/dashboard/settings/accounts/AssignAccountTags.js"));
const AccountQuickCodes = lazy(() => import("./components/dashboard/settings/accounts/AccountQuickCodes.js"));
const AddNewAccountDefaults = lazy(() => import("./components/dashboard/settings/accounts/AddNewAccountDefaults.js"));
const NewAccountDefaults = lazy(() => import("./components/dashboard/settings/accounts/NewAccountDefaults.js"));
const Reportresult = lazy(() => import("./components/dashboard/reports/CaseMangement/Reportresult.js"));
const ReportParameters = lazy(() => import("./components/dashboard/reports/CaseMangement/NewCases.js"));
const AddShippingType = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/shipping/AddShippingType.js"));
const ShippingEdit = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/shipping/ShippingEdit.js"));
const ShippingTypes = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/shipping/ShippingTypes.js"));
const AssignAccounts = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/shipping/AssignShipping.js"));
const Chart = lazy(() => import("./components/chartbot/Chart.js"));
const PreferencesTab = lazy(() => import("./components/header/hiraDentalLab/labDetails/preferences.js"));
const ReReprint = lazy(() => import("./components/dashboard/finances/statement/rereprint.js"));
const PreviewStatement = lazy(() => import("./components/dashboard/finances/statement/preview.js"));
const Editdetails = lazy(() => import("./components/dashboard/finances/statement/editdetails.js"));
const PinSetup = lazy(() => import("./components/header/hiraDentalLab/labDetails/pin/PinSetup.jsx"));
const StatementEmail = lazy(() => import("./components/header/hiraDentalLab/labDetails/pin/StatementEmail.jsx"));
const AddnextCase = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/nextCase/AddnextCase.js"));
const Editnextcase = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/general/nextCase/Editnextcase.js"));
const Credit = lazy(() => import("./components/dashboard/finances/posting/Credit.js"));
const SmopCases = lazy(() => import("./components/dashboard/cases/SmopCases.js"));
const LotsearchReport = lazy(() => import("./components/dashboard/reports/lotsearchAdd.js"));
const Addtaxes = lazy(() => import("./components/dashboard/reports/Taxes/Addtaxes.js"));
const Addaccounttaxes = lazy(() => import("./components/dashboard/reports/Taxes/AddAccountTaxes.js"));
const Addproceduretaxes = lazy(() => import("./components/dashboard/reports/Taxes/Addproceduretaxes.js"));
const Addpaymentmethod = lazy(() => import("./components/dashboard/reports/Payments/Addpaymentmethod.js"));
const Addpaymentaccounts = lazy(() => import("./components/dashboard/reports/Payments/Addpaymentaccounts.js"));
const Addpaymentprocess = lazy(() => import("./components/dashboard/reports/Payments/Addpaymentprocess.js"));
const AddNextCasesCost = lazy(() => import("./components/dashboard/reports/Costs/AddNextCasesCost.js"));
const AddNextCaseAccount = lazy(() => import("./components/dashboard/reports/Costs/AddNextCaseAccount.js"));
const AddNextCasesByDesp = lazy(() => import("./components/dashboard/reports/Costs/AddNextCasesByDesp.js"));
const AddNextCasesByType = lazy(() => import("./components/dashboard/reports/Costs/AddNextCaseByType.js"));
const AddInternalCaseReason = lazy(() => import("./components/dashboard/reports/Costs/AddInternalCaseReason.js"));
const AddPayPerDetailTable = lazy(() => import("./components/dashboard/reports/Costs/AddpayperDetails.js"));
const AddpayperDetails = lazy(() => import("./components/dashboard/reports/Costs/AddpayperDetails.js"));
const Addpaypertask = lazy(() => import("./components/dashboard/reports/Costs/Addpaypertask.js"));
const Addturntime = lazy(() => import("./components/dashboard/reports/CaseMangement/Addturntime.js"));
const AddStatement = lazy(() => import("./components/dashboard/reports/Finances/AddStatement.js"));
const AddMoneyowed = lazy(() => import("./components/dashboard/reports/Finances/AddMoneyowed.js"));
const AddRevenuBatchPrint = lazy(() => import("./components/dashboard/reports/Finances/AddRevenueBatchPrint.js"));
const AddRevenue = lazy(() => import("./components/dashboard/reports/Finances/AddRevenue.js"));
const AddRevenueCredit = lazy(() => import("./components/dashboard/reports/Finances/AddRevenueCredit.js"));
const AddRevnuecenter = lazy(() => import("./components/dashboard/reports/Finances/AddRevenue.js"));
const Addcomponentaccount = lazy(() => import("./components/dashboard/reports/Sales/Addcomponentaccount.js"));
const Addcomponent = lazy(() => import("./components/dashboard/reports/Sales/Addcomponent.js"));
const Addunitsale = lazy(() => import("./components/dashboard/reports/Sales/Addunitsale.js"));
const Addproductcomparison = lazy(() => import("./components/dashboard/reports/Sales/Addproductcomparison"));
const Addalloy = lazy(() => import("./components/dashboard/reports/Sales/Addalloy.js"));
const Addsalereports = lazy(() => import("./components/dashboard/reports/Sales/Addsalereports.js"));
const Addproduct = lazy(() => import("./components/dashboard/reports/Sales/Addproduct.js"));
const Addcustomersale = lazy(() => import("./components/dashboard/reports/Sales/Addcustomersale.js"));
const Addcomparison = lazy(() => import("./components/dashboard/reports/Sales/Addcomparison.js"));
const AddRevenuePrice = lazy(() => import("./components/dashboard/reports/Finances/AddRevenuePrice.js"));
const AddRevenuecenter = lazy(() => import("./components/dashboard/reports/Finances/AddRevenuecenter.js"));
const Addincompletetask = lazy(() => import("./components/dashboard/reports/CaseMangement/Addincompletetask.js"));
const Addcompletetask = lazy(() => import("./components/dashboard/reports/CaseMangement/Addcompletetask.js"));
const Addimprovement = lazy(() => import("./components/dashboard/reports/CaseMangement/AddImprovement.js"));
const Addcasetags = lazy(() => import("./components/dashboard/reports/CaseMangement/Addcasetags.js"));
const Addtagged = lazy(() => import("./components/dashboard/reports/CaseMangement/AddTagged.js"));
const Addpatient = lazy(() => import("./components/dashboard/reports/CaseMangement/Addpatient.js"));
const Addunitaccount = lazy(() => import("./components/dashboard/reports/Sales/Addunitaccount.js"));
const OutsourcedCases = lazy(() => import("./components/dashboard/cases/byMonth/OutsourcedCases.js"));
const Paymentreceipt = lazy(()=> import( "./components/dashboard/finances/posting/Paymentreceipt"))
const AllRemindersPage = lazy( ()=> import ("./pages/AllReminders/AllRemindersPage"));
const AddDepartment = lazy( ()=> import ("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/department/AddDepartment"));
const EditDepartment = lazy( ()=> import ("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/department/EditDepartment"));
const AddTask = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/task/AddTask"))
// const CaseTasks = lazy(()=> import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/tasks/CaseTasks"))
// const ProgressStages = lazy(()=> import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/tasks/ProgressStages"))
// const AddProgressStage = lazy(()=> import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/tasks/AddProgressStage"))
// const ScanningAndAssigning = lazy(()=> import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/tasks/ScanningAndAssigning"))
const AssignTechnicians = lazy(()=> import("./components/dashboard/reports/CaseMangement/AssignTechnicians"))
const EditTask = lazy(() => import("./components/dashboard/settings/caseSetting/Tabs/procedures/labprocedure/task/EditTask"));
const Printing = lazy(()=> import("./components/home/Printing/Printing"))


const App = () => {
  return (
    <AuthProvider>
      <CurrencyProvider>
          <UploadManagerProvider>
        <BrowserRouter>
          <Suspense fallback={<div style={{ padding: 24 }}>Loading...</div>}>
              <ReminderProvider>
            <Routes>
            <Route element={<PublicRoute />}>
              <Route path="/" element={<SigninForm />} />
              <Route path="/signup" element={<Register />} />
              <Route path="/signup-2" element={<SignUpStep2 />} />
              <Route path="/signup-3" element={<Signupstep3 />} />
              <Route path="/reset" element={<PasswordReset />} />
              <Route
                path="/reset-password/:userId"
                element={<ResetPasswordConfirm />}
              />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route
                path="/home"
                element={
                  <ProtectedRouteWithPermission path="/home" element={Home} />
                }
              />
              <Route
                path="/keyboard"
                element={
                  <ProtectedRouteWithPermission
                    path="/keyboard"
                    element={Keyboard}
                  />
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRouteWithPermission
                    path="/support"
                    element={Support}
                  />
                }
              />
              <Route
                path="/completeTasks"
                element={
                  <ProtectedRouteWithPermission
                    path="/completeTasks"
                    element={CompleteTask}
                  />
                }
              />
              <Route path="/report-result" element={<Reportresult />} />
              <Route
                path="/simpleMode"
                element={
                  <ProtectedRouteWithPermission
                    path="/simpleMode"
                    element={SimpleMode}
                  />
                }
              />
              <Route
                path="/driverPage"
                element={
                  <ProtectedRouteWithPermission
                    path="/driverPage"
                    element={DriverPage}
                  />
                }
              />
              <Route
                path="/user/:userId"
                element={
                  <ProtectedRouteWithPermission path="/user" element={User} />
                }
              />
              <Route
                path="/addAccount"
                element={
                  <ProtectedRouteWithPermission
                    path="/addAccount"
                    element={AddAccount}
                  />
                }
              />
              <Route
                path="/editUser/:userId"
                element={
                  <ProtectedRouteWithPermission
                    path="/editUser/:userId"
                    element={EditUser}
                  />
                }
              />
              <Route
                path="/editauthentication"
                element={
                  <ProtectedRouteWithPermission
                    path="/editauthentication"
                    element={EditAuthentication}
                  />
                }
              />
              <Route
                path="/newCases"
                element={
                  <ProtectedRouteWithPermission
                    path="/newCases"
                    element={ReportParameters}
                  />
                }
              />
              <Route
                path="/calender"
                element={
                  <ProtectedRouteWithPermission
                    path="/calender"
                    element={Calender}
                  />
                }
              />
              {/* Manual routes without permission checks */}
              <Route path="/Mannual" element={<ManualsPage />} />
              <Route path="/manual-editor" element={<ManualEditor />} />
              <Route
                path="/file"
                element={
                  <ProtectedRouteWithPermission path="/file" element={File} />
                }
              />
              <Route
                path="/addNewfile"
                element={
                  <ProtectedRouteWithPermission
                    path="/addNewfile"
                    element={Addnewfile}
                  />
                }
              />
              <Route
                path="/labSetting"
                element={
                  <ProtectedRouteWithPermission
                    path="/labSetting"
                    element={LabSetting}
                  />
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRouteWithPermission path="/users" element={Users} />
                }
              />
              <Route
                path="/AddNewUser"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddNewUser"
                    element={AddNewUser}
                  />
                }
              />
              <Route
                path="/tabs"
                element={
                  <ProtectedRouteWithPermission
                    path="/tabs"
                    element={DetailTabs}
                  />
                }
              />
              <Route
                path="/partner-labs"
                element={
                  <ProtectedRouteWithPermission
                    path="/partner-labs"
                    element={PartnerLab}
                  />
                }
              />
              <Route
                path="/overview"
                element={
                  <ProtectedRouteWithPermission
                    path="/overview"
                    element={Overview}
                  />
                }
              />
              <Route path="/accountDetails" element={<AccountDetails />} />
              <Route path="/accountDetails/:caseId" element={<AccountDetails />} />
              <Route
                path="/newPlanEstimate"
                element={
                  <ProtectedRouteWithPermission
                    path="/newPlanEstimate"
                    element={NewPlanEstimate}
                  />
                }
              />
              <Route
                path="/newCasePickup"
                element={
                  <ProtectedRouteWithPermission
                    path="/newCasePickup"
                    element={NewCasePickup}
                  />
                }
              />
              <Route
                path="/casesOpened"
                element={
                  <ProtectedRouteWithPermission
                    path="/casesOpened"
                    element={CasesOpened}
                  />
                }
              />
              <Route
                path="/casesArrive"
                element={
                  <ProtectedRouteWithPermission
                    path="/casesArrive"
                    element={CasesArrive}
                  />
                }
              />
              <Route
                path="/caseship"
                element={
                  <ProtectedRouteWithPermission
                    path="/caseship"
                    element={CaseShip}
                  />
                }
              />
              <Route
                path="/creatShipment"
                element={
                  <ProtectedRouteWithPermission
                    path="/creatShipment"
                    element={CreatShipment}
                  />
                }
              />
              <Route
                path="/openTryInCases"
                element={
                  <ProtectedRouteWithPermission
                    path="/openTryInCases"
                    element={OpenTryInCases}
                  />
                }
              />
              <Route
                path="/casesOnHold"
                element={
                  <ProtectedRouteWithPermission
                    path="/casesOnHold"
                    element={CasesOnHold}
                  />
                }
              />
              <Route
                path="/toOutsource"
                element={
                  <ProtectedRouteWithPermission
                    path="/toOutsource"
                    element={ToOutsource}
                  />
                }
              />
              <Route
                path="/caseEstimates"
                element={
                  <ProtectedRouteWithPermission
                    path="/caseEstimates"
                    element={CaseEstimates}
                  />
                }
              />
              <Route
                path="/account/:accountId/contacts/edit/:contactId"
                element={
                  <ProtectedRouteWithPermission
                    path="/account/:accountId/contacts/edit/:contactId"
                    element={EditContactButton}
                  />
                }
              />
              <Route
                path="/openCases"
                element={
                  <ProtectedRouteWithPermission
                    path="/openCases"
                    element={OpenCases}
                  />
                }
              />
              <Route
                path="/lotsearchAdd"
                element={
                  <ProtectedRouteWithPermission
                    path="/lotsearchAdd"
                    element={LotsearchReport}
                  />
                }
              />
              <Route
                path="/Addtaxes"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addtaxes"
                    element={Addtaxes}
                  />
                }
              />
              <Route
                path="/Addpaymentaccounts"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addpaymentaccounts"
                    element={Addpaymentaccounts}
                  />
                }
              />
              <Route
                path="/AddAccountTaxes"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddAccountTaxes"
                    element={Addaccounttaxes}
                  />
                }
              />
              <Route
                path="/Addproceduretaxes"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addproceduretaxes"
                    element={Addproceduretaxes}
                  />
                }
              />
              <Route
                path="/Addpaymentprocess"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addpaymentprocess"
                    element={Addpaymentprocess}
                  />
                }
              />
              <Route
                path="/Addpaymentmethod"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addpaymentmethod"
                    element={Addpaymentmethod}
                  />
                }
              />
              <Route
                path="/AddNextCasesCost"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddNextCasesCost"
                    element={AddNextCasesCost}
                  />
                }
              />
              <Route
                path="/AddNextCaseAccount"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddNextCaseAccount"
                    element={AddNextCaseAccount}
                  />
                }
              />
              <Route
                path="/AddNextCasesByDesp"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddNextCasesByDesp"
                    element={AddNextCasesByDesp}
                  />
                }
              />
              <Route
                path="/AddNextCaseByType"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddNextCaseByType"
                    element={AddNextCasesByType}
                  />
                }
              />
              <Route
                path="/AddpayperDetails"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddpayperDetails"
                    element={AddpayperDetails}
                  />
                }
              />
              <Route
                path="/Addpaypertask"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addpaypertask"
                    element={Addpaypertask}
                  />
                }
              />
              <Route
                path="/Addmoneyowed"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addmoneyowed"
                    element={AddMoneyowed}
                  />
                }
              />
              <Route
                path="/Addstatement"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addstatement"
                    element={AddStatement}
                  />
                }
              />
              <Route
                path="/AddRevenueBatchPrint"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddRAddRevenueBatchPrintevenue"
                    element={AddRevenuBatchPrint}
                  />
                }
              />
              <Route
                path="/AddRevenue"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddRevenue"
                    element={AddRevenue}
                  />
                }
              />
              <Route
                path="/AddRevenueCredit"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddRevenueCredit"
                    element={AddRevenueCredit}
                  />
                }
              />
              <Route
                path="/Addcomponentaccount"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcomponentaccount"
                    element={Addcomponentaccount}
                  />
                }
              />
              <Route
                path="/Addcomponent"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcomponent"
                    element={Addcomponent}
                  />
                }
              />
              <Route
                path="/AddSalesReport"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddSalesReport"
                    element={AddSalesReport}
                  />
                }
              />
              <Route
                path="/Addproductcomparison"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addproductcomparison"
                    element={Addproductcomparison}
                  />
                }
              />
              <Route
                path="/Addalloy"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addalloy"
                    element={Addalloy}
                  />
                }
              />
              <Route
                path="/Addproduct"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addalloy"
                    element={Addproduct}
                  />
                }
              />
              <Route
                path="/Addcustomersale"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcustomersale"
                    element={Addcustomersale}
                  />
                }
              />
              <Route
                path="/price-list-results"
                element={
                  <ProtectedRouteWithPermission
                    path="/price-list-results"
                    element={AddRevenuePrice}
                  />
                }
              />
              <Route
                path="/add-revenue-center"
                element={
                  <ProtectedRouteWithPermission
                    path="/add-revenue-center"
                    element={AddRevenuecenter}
                  />
                }
              />
              <Route
                path="/Addunitaccount"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addunitaccount"
                    element={Addunitaccount}
                  />
                }
              />
              <Route
                path="/undelivarableCases"
                element={
                  <ProtectedRouteWithPermission
                    path="/undelivarableCases"
                    element={UndeliverableCases}
                  />
                }
              />
              <Route
                path="/AddInternalCaseReason"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddInternalCaseReason"
                    element={AddInternalCaseReason}
                  />
                }
              />
              <Route
                path="/sticker/:caseId"
                element={
                  <ProtectedRouteWithPermission
                    path="/sticker/:caseId"
                    element={PrintWorkTicketSticker}
                  />
                }
              />
                            <Route
                path="/workticket/:caseId"
                element={
                  <ProtectedRouteWithPermission
                    path="/workticket/:caseId"
                    element={PrintWorkTicket}
                  />
                }
              />
              <Route
                path="/print-tickets"
                element={
                  <ProtectedRouteWithPermission
                    path="/print-tickets"
                    element={PrintWorkTicket}
                  />
                }
              />
              <Route
                path="/patientDetails"
                element={
                  <ProtectedRouteWithPermission
                    path="/patientDetails"
                    element={AccountDetails}
                  />
                }
              />
              <Route
                path="/patientrecords"
                element={
                  <ProtectedRouteWithPermission
                    path="/patientrecords"
                    element={PatientRecord}
                  />
                }
              />
              <Route
                path="/case/:caseId"
                element={
                  <ProtectedRouteWithPermission
                    path="/case/:caseId"
                    element={Case}
                  />
                }
              />
              <Route
                path="/searchAddPage"
                element={
                  <ProtectedRouteWithPermission
                    path="/searchAddPage"
                    element={SearchAddPage}
                  />
                }
              />
              <Route
                path="/accountTable"
                element={
                  <ProtectedRouteWithPermission
                    path="/accountTable"
                    element={AccountTable}
                  />
                }
              />
              <Route
                path="/account/:accountId/contacts/add"
                element={
                  <ProtectedRouteWithPermission
                    path="/account/:accountId/contacts/add"
                    element={AddContactButton}
                  />
                }
              />
              <Route
                path="/tabsWorking/:accountId"
                element={
                  <ProtectedRouteWithPermission
                    path="/tabsWorking/:accountId"
                    element={TabsWorking}
                  />
                }
              />
              <Route
                path="/overview"
                element={
                  <ProtectedRouteWithPermission
                    path="/overview"
                    element={SettingOverview}
                  />
                }
              />
              <Route
                path="/financeOwerview"
                element={
                  <ProtectedRouteWithPermission
                    path="/financeOwerview"
                    element={FinanceOwerview}
                  />
                }
              />
              <Route
                path="/posting"
                element={
                  <ProtectedRouteWithPermission
                    path="/posting"
                    element={Posting}
                  />
                }
              />
              <Route
                path="/credits/:id"
                element={
                  <ProtectedRouteWithPermission
                    path="/credits"
                    element={Credit}
                  />
                }
              />
               <Route
                path="/payments/:id"
                element={
                  <ProtectedRouteWithPermission
                    path="/payments/:id"
                    element={Paymentreceipt}
                  />
                }
              />
              <Route
                path="/statement"
                element={
                  <ProtectedRouteWithPermission
                    path="/statement"
                    element={Statement}
                  />
                }
              />
              <Route
                path="/caseSetting"
                element={
                  <ProtectedRouteWithPermission
                    path="/caseSetting"
                    element={CaseSetting}
                  />
                }
              />
                <Route
  path="/lab/settings/*"
  element={
    <ProtectedRouteWithPermission path="/caseSetting" element={CaseSetting} />
  }
/>
              <Route
                path="/promtoionOverview"
                element={
                  <ProtectedRouteWithPermission
                    path="/promtoionOverview"
                    element={PromotionOverview}
                  />
                }
              />
              <Route
                path="/website"
                element={
                  <ProtectedRouteWithPermission
                    path="/website"
                    element={DDXWebsites}
                  />
                }
              />
              <Route
                path="/ddxAdd"
                element={
                  <ProtectedRouteWithPermission path="/ddxAdd" element={DDXAdd} />
                }
              />
              <Route
                path="/addButton"
                element={
                  <ProtectedRouteWithPermission
                    path="/addButton"
                    element={AddButton}
                  />
                }
              />
              <Route
                path="/coupon"
                element={
                  <ProtectedRouteWithPermission path="/coupon" element={Coupon} />
                }
              />

              <Route
                path="/addcoupon"
                element={
                  <ProtectedRouteWithPermission
                    path="/addcoupon"
                    element={AddCoupon}
                  />
                }
              />
              <Route
                path="/widgets"
                element={
                  <ProtectedRouteWithPermission
                    path="/widgets"
                    element={Widgets}
                  />
                }
              />
              <Route
                path="/ddxAccount"
                element={
                  <ProtectedRouteWithPermission
                    path="/ddxAccount"
                    element={DDXAccount}
                  />
                }
              />
              <Route
                path="/invoices/:invoiceNumber"
                element={
                  <ProtectedRouteWithPermission
                    path="/invoices/:invoiceNumber"
                    element={InvoiceDetail}
                  />
                }
              />
              <Route
                path="/patientrecords/:id"
                element={
                  <ProtectedRouteWithPermission
                    path="/patientrecords/:id"
                    element={PatientRecord}
                  />
                }
              />
              <Route
                path="/Addlabprocedure"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addlabprocedure"
                    element={AddLabProcedureReportTable}
                  />
                }
              />
              <Route
                path="/add-category"
                element={
                  <ProtectedRouteWithPermission
                    path="/add-category"
                    element={AddCategory}
                  />
                }
              />
              <Route
                path="/invoices/:invoiceNumber/edit"
                element={
                  <ProtectedRouteWithPermission
                    path="/invoices/:invoiceNumber/edit"
                    element={EditInvoice}
                  />
                }
              />
              <Route
                path="/PermissionGroups"
                element={
                  <ProtectedRouteWithPermission
                    path="/PermissionGroups"
                    element={PermissionGroups}
                  />
                }
              />
              <Route
                path="/AddPermissionGroup"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddPermissionGroup"
                    element={AddPermissionGroup}
                  />
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRouteWithPermission
                    path="/reports"
                    element={Reports}
                  />
                }
              />
              <Route
                path="/Addsalereport"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addsalereport"
                    element={Addsalereports}
                  />
                }
              />
              <Route
                path="/AddSalesReport"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddSalesReport"
                    element={AddSalesReport}
                  />
                }
              />
              <Route
                path="/Addcaseshipped"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcaseshipped"
                    element={Addshipping}
                  />
                }
              />
                  <Route
                path="/printing"
                element={
                  <ProtectedRouteWithPermission
                    path="/printing"
                    element={Printing}
                  />
                }
              />
              <Route
                path="/Addturntime"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addturntime"
                    element={Addturntime}
                  />
                }
              />
              <Route
                path="/Addincompletetask"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addincompletetask"
                    element={Addincompletetask}
                  />
                }
              />
              <Route
                path="/Addcompletetask"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcompletetask"
                    element={Addcompletetask}
                  />
                }
              />
              <Route
                path="/AddImprovement"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddImprovement"
                    element={Addimprovement}
                  />
                }
              />
              <Route
                path="/AddCaseTags"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddCaseTags"
                    element={Addcasetags}
                  />
                }
              />
              <Route
                path="/Addcasetags"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcasetags"
                    element={Addtagged}
                  />
                }
              />
              <Route
                path="/Addpatient"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addpatient"
                    element={Addpatient}
                  />
                }
              />
              <Route
                path="/reprint-statement"
                element={
                  <ProtectedRouteWithPermission
                    path="/reprint-statement"
                    element={Reprintbutton}
                  />
                }
              />
              <Route
                path="/rereprint-statement"
                element={
                  <ProtectedRouteWithPermission
                    path="/rereprint-statement"
                    element={ReReprint}
                  />
                }
              />
              <Route
                path="/reprint-Statements"
                element={
                  <ProtectedRouteWithPermission
                    path="/reprint-Statements"
                    element={ReprintStatements}
                  />
                }
              />
              <Route
                path="/statements/by-date/:runDate"
                element={
                  <ProtectedRouteWithPermission
                    path="/statements/by-date/:runDate"
                    element={Summary}
                  />
                }
              />
              <Route
                path="/runStatements"
                element={
                  <ProtectedRouteWithPermission
                    path="/runStatements"
                    element={RunStatements}
                  />
                }
              />
              <Route
                path="/run-date-range-statement"
                element={
                  <ProtectedRouteWithPermission
                    path="/run-date-range-statement"
                    element={RunDateRangeStatement}
                  />
                }
              />
              <Route
                path="/preview-statement/:previewId"
                element={
                  <ProtectedRouteWithPermission
                    path="/preview-statement/:previewId"
                    element={PreviewStatement}
                  />
                }
              />
              <Route
                path="/Addunitsale"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addunitsale"
                    element={Addunitsale}
                  />
                }
              />
              <Route
                path="/edit-statement"
                element={
                  <ProtectedRouteWithPermission
                    path="/edit-statement"
                    element={Editdetails}
                  />
                }
              />
              <Route
                path="/Addinvoice"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addinvoice"
                    element={Addinvoiceitem}
                  />
                }
              />
              <Route
                path="/Addaccountsale"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addaccountsale"
                    element={Addaccountsales}
                  />
                }
              />
              <Route
                path="/Addcomparison"
                element={
                  <ProtectedRouteWithPermission
                    path="/Addcomparison"
                    element={Addcomparison}
                  />
                }
              />
              <Route
                path="/AddnextCase"
                element={
                  <ProtectedRouteWithPermission
                    path="/AddnextCase"
                    element={AddnextCase}
                  />
                }
              />
              <Route
                path="/smopCases"
                element={
                  <ProtectedRouteWithPermission
                    path="/smopCases"
                    element={SmopCases}
                  />
                }
              />
              <Route
                path="/Editnextcase/:id"
                element={
                  <ProtectedRouteWithPermission
                    path="/Editnextcase/:id"
                    element={Editnextcase}
                  />
                }
              />
                            <Route
                path="BatchPrintWorkTicketsForm"
                element={<BatchPrintWorkTicketsForm />}
              />
              <Route path="/ticketsform" element={<TicketsForm />} />
                 <Route path="/money-owed-overdue" element={<MoneyOwedOverdue />} />
              <Route path="/barcodes" element={<BarcodeList />} />
              <Route
                path="/edit-permissions/:userId"
                element={
                  <ProtectedRouteWithPermission
                    path="/edit-permissions/:userId"
                    element={EditPermission}
                  />
                }
              />
              <Route path="/outsourcedCases" element={<OutsourcedCases />} />
              <Route
                path="/cases-cancelled-report"
                element={<CasesCancelledReportView />}
              />
              <Route path="/addshipping" element={<Addshipping />} />
              <Route path="/AddCaseFile" element={<CaseFiles />} />
              <Route path="/edit-attribute-price" index element={<EditAttributePrices />} />
              <Route path="/edit-price-lists" index element={<EditPriceLists />} />
              <Route path="/price-list-table" index element={<PriceListTable />} />
              <Route path="/procedure-quick-codes" index element={<ProcedureQuickCodes />} />
              <Route path="/procedure-groups" element={<ProcedureGroups />} />
              <Route path="/add-procedure-group" element={<AddProcedureGroup />} />
              <Route path="/multi-edit" element={<MultiEdit />} />
              <Route path="/assign" element={<AssignPrice/>} />
              <Route path="/assign-procedure" element={<AssignProcedure />} />
              <Route path="/assign-shipping" element={<AssignShippingService />} />
              <Route path="/assign-tags" element={<AssignAccountTags />} />
              <Route path="/account-quick" element={<AccountQuickCodes />} />
              <Route path="/account-default" element={<NewAccountDefaults />} />
              <Route path="/add-account-default" element={<AddNewAccountDefaults/>} />
              <Route path="/AddShippingType" element={<AddShippingType/>} />
              <Route path="/shipping-edit/:id" element={<ShippingEdit/>} />
              {/*<Route path="/shipping-types" element={<ShippingTypes/>} />*/}
              <Route path="/assign-accounts/:id" element={<AssignAccounts />} />
              {/* PIN and Statement Email Routes */}
              <Route path="/pin-setup/:userId" element={<PinSetup />} />
              <Route path="/statement-email/:userId" element={<StatementEmail />} />
              {/* Preferences Route */}
              <Route path="/preferences"
                element={
                  <ProtectedRouteWithPermission
                    path="/preferences"
                    element={PreferencesTab}
                  />
                }
              />
                <Route
                    path="/reminders"
                    element={
                    <ProtectedRouteWithPermission
                        path="/reminders"
                        element={AllRemindersPage}
                    />
                }
                />
          <Route
  path="/add-department"
  element={
    <ProtectedRouteWithPermission
      path="/add-department"
      element={AddDepartment}
    />
  }
/>

<Route
  path="/edit-department/:id"
  element={
    <ProtectedRouteWithPermission
      path="/edit-department/:id"
      element={EditDepartment}
    />
  }
/>

<Route
  path="/add-task"
  element={
    <ProtectedRouteWithPermission
      path="/add-task"
      element={AddTask}
    />
  }
/>             <Route
  path="/edit-task"
  element={
    <ProtectedRouteWithPermission path="/edit-task" element={EditTask} />
  }
/>


{/* <Route
  path="/case-tasks"
  element={
    <ProtectedRouteWithPermission
      path="/case-tasks"
      element={CaseTasks}
    />
  }
/> */}

{/* <Route
  path="/progress-stages"
  element={
    <ProtectedRouteWithPermission
      path="/progress-stages"
      element={ProgressStages}
    />
  }
/> */}

{/* <Route
  path="/add-progress-stage"
  element={
    <ProtectedRouteWithPermission
      path="/add-progress-stage"
      element={AddProgressStage}
    />
  }
/> */}

{/* <Route
  path="/scanning-assigning"
  element={
    <ProtectedRouteWithPermission
      path="/scanning-assigning"
      element={ScanningAndAssigning}
    />
  }
/> */}
                <Route
                path="/assign-technician/:date"
                element={
                  <ProtectedRouteWithPermission
                    path="/assign-technician/:date"
                    element={AssignTechnicians}
                  />
                }
              />
            </Route>
            </Routes>
              </ReminderProvider>
          </Suspense>
          {/* <Chart /> */}
        </BrowserRouter>
        </UploadManagerProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
};

export default App;